







































import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import useSystemSettings from "@/hooks/useSystemSettings";
import { yMetrics } from "@/lib/analytics";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCost, getFullPriceData } from "@/lib/moneyHelpers";
import PaymentManager from "@/payments";
import { EVENTS, PurchaseType } from "@/const";
import BaseSpinner from "@/components/BaseSpinner";
import CardCost from "@/components/CardCost";
import ModalView from "@/components/ModalView";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import { PaymentConfirmationData } from "@/components/PaymentConfirmation/const";

export default defineComponent({
  components: { PaymentConfirmation, ModalView, BaseSpinner, CardCost },
  name: "ModalRefillBalance",
  setup() {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.DEPOSIT]);
    const { hasActivePaymentSystem } = useSystemSettings();

    const show = ref(false);
    const loading = ref(false);
    const selectedPackage = ref<PaymentPackage | null>(null);
    const processing = ref(false);
    const paymentSystemId = ref<string>();

    const priceData = computed(() => getFullPriceData(selectedPackage.value));

    const showModal = async () => {
      show.value = true;
      try {
        loading.value = true;
        await Promise.all([getPaymentPackages()]);
      } finally {
        loading.value = false;
      }
    };

    const getCurrencyCost = (paymentPackage: PaymentPackage) => {
      if (paymentPackage.costs.length === 1 && paymentPackage.costs[0].cost === paymentPackage.amount) return [];
      return paymentPackage.costs.map(cost => {
        return formatMoneyString(cost.cost, { currencyCode: cost.currency });
      });
    };

    const closeModal = () => {
      show.value = false;
      paymentSystemId.value = undefined;
      selectedPackage.value = null;
    };

    const purchase = async (data: PaymentConfirmationData) => {
      paymentSystemId.value = data.paymentSystemId;
      if (!hasActivePaymentSystem.value) throw new Error("Payment system not found");
      if (processing.value || !selectedPackage.value || !data.paymentSystemId) return;
      try {
        processing.value = true;
        const response = await PaymentManager.purchase({
          pricePackageId: selectedPackage.value.id,
          paymentSystemId: data.paymentSystemId,
          price: priceData.value.price,
        });
        response?.transaction && emitter.emit(EVENTS.BALANCE_REFILL_PURCHASED, response.transaction);

        if (response.status) {
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("Thank you for your purchase"),
            position: "top",
          });
        }
        yMetrics.paymentPurchase();
        closeModal();
      } finally {
        processing.value = false;
      }
    };

    const packageClick = async (paymentPackage: PaymentPackage) => {
      selectedPackage.value = paymentPackage;
      if (paymentSystemId.value) await purchase({ fromBalance: false, paymentSystemId: paymentSystemId.value });
    };

    emitter.on<void>(EVENTS.BALANCE_REFILL_SHOW, showModal);

    onBeforeUnmount(() => {
      emitter.off<void>(EVENTS.BALANCE_REFILL_SHOW, showModal);
    });

    return {
      show,
      paymentPackages,
      loading,
      processing,
      priceData,
      selectedPackage,
      PurchaseType,
      packageClick,
      getCost,
      getCurrencyCost,
      purchase,
      closeModal,
    };
  },
});
